import type WhiteboardController from '../controllers/WhiteboardController';
import type SimpleSelection from '../fabric/SimpleSelection';
import { isConnector } from '../objects/guards';

const getWidth = (object: fabric.Object) => {
  return (
    (isConnector(object)
      ? object.calcDimensions().width
      : object.width * object.scaleX) + object.strokeWidth
  );
};

const getHeight = (object: fabric.Object) => {
  return (
    (isConnector(object)
      ? object.calcDimensions().height
      : object.height * object.scaleY) + object.strokeWidth
  );
};

function distributeItems(
  controller: WhiteboardController,
  selection: SimpleSelection,
  alignment: 'horizontal' | 'vertical'
) {
  const canvas = controller.canvas;

  if (
    !selection ||
    selection.type !== 'activeSelection' ||
    selection.size() <= 1
  ) {
    return;
  }

  const { width, height } = selection;

  // Sort the objects based on position so that distributing doesn't reorder
  // them.
  const sortedObjects = selection
    .getObjects()
    .toSorted((a, b) =>
      alignment === 'horizontal' ? a.left - b.left : a.top - b.top
    );

  // Add the widths/heights of all objects in order to compute the relative
  // offset for the distribution phase.
  let totalSize = 0;
  sortedObjects.forEach(object => {
    switch (alignment) {
      case 'horizontal':
        totalSize += getWidth(object);
        break;
      case 'vertical':
        totalSize += getHeight(object);
        break;
    }
  });

  let offset: number;
  let pos: number;
  switch (alignment) {
    case 'horizontal':
      offset = (width - totalSize) / (selection.size() - 1);
      pos = -width / 2;
      break;
    case 'vertical':
      offset = (height - totalSize) / (selection.size() - 1);
      pos = -height / 2;
      break;
  }

  sortedObjects.forEach(actualObject => {
    switch (alignment) {
      case 'horizontal':
        actualObject.moveToCoords({
          left: selection.left + selection.width / 2 + pos,
        });
        pos += offset + getWidth(actualObject);
        break;
      case 'vertical':
        actualObject.moveToCoords({
          top: selection.top + selection.height / 2 + pos,
        });
        pos += offset + getHeight(actualObject);
        break;
    }
  });

  // Force bounds recalculation in selection
  selection.addWithUpdate();
  canvas.renderAll();
}

function alignItems(
  controller: WhiteboardController,
  selection: SimpleSelection,
  alignment: 'top' | 'left' | 'bottom' | 'right' | 'center' | 'middle'
) {
  const canvas = controller.canvas;

  if (!selection || selection.type !== 'activeSelection') {
    return;
  }

  // selection._alignmentOperation = true;

  selection._objects.forEach(object => {
    const { top, left, width, height } = selection;

    switch (alignment) {
      case 'top':
        object.moveToCoords({ top });
        break;
      case 'left':
        object.moveToCoords({ left });
        break;
      case 'bottom':
        object.moveToCoords({
          top:
            top + height - object.height * object.scaleY - object.strokeWidth,
        });
        break;
      case 'right':
        object.moveToCoords({
          left:
            left + width - object.width * object.scaleX - object.strokeWidth,
        });
        break;
      case 'center':
        object.moveToCoords({
          left: left + width / 2 - (object.width * object.scaleX) / 2,
        });
        break;
      case 'middle':
        object.moveToCoords({
          top: top + height / 2 - (object.height * object.scaleY) / 2,
        });
        break;
    }
  });

  // Force bounds recalculation in selection
  selection.addWithUpdate();
  canvas.renderAll();
}

export default {
  distributeItems,
  alignItems,
};
